import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ListTree, LogOut } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DecisionSheetView } from '@apm/contracts';
import { Button } from '../components/ui/button';
import { useAuth } from '../features/auth/auth-context';
import { decisionSheetApi, decisionSheetKeys } from '../features/decision-sheets/api';
import { DecisionSheetPage } from '../features/decision-sheets/components/decision-sheet-page';
import {
  PublicSheetLoading,
  DecisionOutlinePanel,
  OUTLINE_PREF_KEY,
  readOutlinePref,
  useDisplayFont,
  useGlassGlow,
  useWide,
} from '../features/decision-sheets/components/public-shell-parts';
import { decisionSections } from '../features/decision-sheets/model';
import { useOrgTimezoneSync } from '../lib/org-timezone';
import '../styles/public-shell.css';

/**
 * Sổ chốt phương án mở trong cửa sổ riêng — nút "Mở trong cửa sổ mới" ở chế độ
 * Xem (người dùng 2026-10-07: "mở ra link open bằng giao diện giống như link
 * chia sẻ"; rồi: bỏ thanh lọc, thêm mục lục và "Bạn là ai?" như link chia sẻ).
 *
 * Khung là **khung của trang chia sẻ** `/d/$token` — dùng chung mục lục, kính,
 * phông chữ qua `public-shell-parts` — nhưng dữ liệu và quyền là của **tài khoản
 * đang đăng nhập**: đi thẳng API nội bộ, không cần (và không tạo) link công
 * khai. Người trả lời chốt được ngay ở đây; người khác chỉ xem.
 *
 * Nằm ngoài `_app` để không có thanh điều hướng của app. Chưa đăng nhập ⇒ mời
 * đăng nhập rồi quay lại đúng trang (`?next=`).
 */
export const Route = createFileRoute('/sheet/$sheetId')({ component: StandaloneSheetPage });

function StandaloneSheetPage(): ReactNode {
  const { t } = useTranslation();
  const { sheetId } = Route.useParams();
  const { status, user, logout } = useAuth();
  const signedIn = status === 'authenticated';
  useOrgTimezoneSync(signedIn);
  useDisplayFont();
  useGlassGlow();

  useEffect(() => {
    document.title = `${t('decisionSheet.eyebrow')} · ${t('common.appName')}`;
  }, [t]);

  // Cùng khoá với trang sổ ⇒ chung một lần tải, không gọi thêm API.
  const sheet = useQuery<DecisionSheetView>({
    queryKey: decisionSheetKeys.detail(sheetId),
    queryFn: () => decisionSheetApi.get(sheetId),
    enabled: signedIn,
  });
  const sections = useMemo(
    () => (sheet.data ? decisionSections(sheet.data, 'all') : []),
    [sheet.data],
  );

  const wide = useWide();
  const [outlineOpen, setOutlineOpen] = useState(readOutlinePref);
  const toggleOutline = (): void =>
    setOutlineOpen((open) => {
      try {
        localStorage.setItem(OUTLINE_PREF_KEY, open ? 'closed' : 'open');
      } catch {
        // Không lưu được thì chỉ mất lựa chọn ở lần mở sau.
      }
      return !open;
    });
  const showOutline = outlineOpen && signedIn && sheet.data !== undefined;

  const here = `/sheet/${sheetId}`;
  const switchAccount = (): void => {
    void logout().finally(() => window.location.assign(`/login?next=${encodeURIComponent(here)}`));
  };

  return (
    <div className="public-shell text-foreground">
      <div className="public-shell__ambient" aria-hidden>
        <div className="public-shell__blob public-shell__blob--1" />
        <div className="public-shell__blob public-shell__blob--2" />
        <div className="public-shell__blob public-shell__blob--3" />
      </div>

      <nav className="public-shell__nav" aria-label={t('publicShell.navLabel')}>
        <div className="public-shell__pill public-shell__glass">
          <a href="/projects" className="public-shell__brand" aria-label={t('common.appName')}>
            <img src="/chill-pm-avatar.png" alt="" width={512} height={512} />
          </a>
          <span className="public-shell__kicker">{t('decisionSheet.eyebrow')}</span>
          {signedIn ? (
            <button
              type="button"
              className="public-shell__toggle"
              aria-pressed={outlineOpen}
              aria-controls="decision-standalone-outline"
              onClick={toggleOutline}
              title={t(outlineOpen ? 'publicShell.outlineHide' : 'publicShell.outlineShow')}
            >
              <ListTree aria-hidden className="size-4" />
              <span className="public-shell__toggle-label">{t('publicShell.outline')}</span>
            </button>
          ) : null}
          {signedIn ? (
            <a href="/projects" className="public-shell__cta">
              {t('publicShell.openApp')}
            </a>
          ) : (
            <a href={`/login?next=${encodeURIComponent(here)}`} className="public-shell__cta">
              {t('decisionSheet.standalone.signIn')}
            </a>
          )}
        </div>
      </nav>

      <main
        className="public-shell__main"
        data-outline={showOutline ? (wide ? 'side' : 'float') : undefined}
      >
        {showOutline ? (
          <div id="decision-standalone-outline" className="public-shell__outline-slot">
            <DecisionOutlinePanel
              sections={sections}
              onPick={() => (wide ? undefined : setOutlineOpen(false))}
            />
          </div>
        ) : null}
        <div className="public-shell__sheet">
          {status === 'restoring' ? (
            <PublicSheetLoading />
          ) : signedIn ? (
            <DecisionSheetPage
              // Chỉ để làm mới danh sách sổ sau khi đổi trạng thái — trang tự đọc
              // dự án thật từ view; cửa sổ riêng không có danh sách nào để làm mới.
              projectId=""
              sheetId={sheetId}
              standalone
              onBack={() => window.close()}
              beforeQuestions={
                <WhoCard
                  name={user?.displayName || user?.email || ''}
                  email={user?.email ?? ''}
                  canAnswer={sheet.data?.can.answer === true}
                  open={sheet.data?.status === 'open'}
                  onSwitch={switchAccount}
                />
              }
            />
          ) : (
            <div className="flex flex-col gap-3" data-testid="decision-standalone-signin">
              <h1 className="text-xl font-semibold">{t('decisionSheet.standalone.signInTitle')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('decisionSheet.standalone.signInHint')}
              </p>
              <a
                href={`/login?next=${encodeURIComponent(here)}`}
                className="self-start text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                {t('decisionSheet.standalone.signIn')}
              </a>
            </div>
          )}
        </div>
      </main>

      <footer className="public-shell__footer">
        {t('decisionSheet.public.poweredBy')} · <a href="/landing.html">{t('publicShell.about')}</a>
      </footer>
    </div>
  );
}

/**
 * "Bạn là ai?" của cửa sổ riêng — cùng chỗ, cùng dáng khối của trang chia sẻ
 * (`data-testid="decision-public-who"` để nhận hiệu ứng kính), nhưng người
 * dùng đã đăng nhập nên không có ô tên/email: nói rõ đang trả lời với tư cách
 * ai, có được chốt không, và cho đổi tài khoản.
 */
function WhoCard({
  name,
  email,
  canAnswer,
  open,
  onSwitch,
}: {
  readonly name: string;
  readonly email: string;
  readonly canAnswer: boolean;
  readonly open: boolean;
  readonly onSwitch: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <section
      className="flex flex-col gap-2 rounded-xl border border-primary/30 bg-primary/5 p-4 max-sm:p-3"
      aria-labelledby="decision-standalone-who"
      data-testid="decision-public-who"
    >
      <h2 id="decision-standalone-who" className="text-base font-semibold">
        {t('decisionSheet.public.whoTitle')}
      </h2>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p
          className="min-w-0 text-sm [overflow-wrap:anywhere]"
          data-testid="decision-standalone-me"
        >
          {t('decisionSheet.public.signedInAs', { name })}
          {email && email !== name ? (
            <span className="text-muted-foreground"> · {email}</span>
          ) : null}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={onSwitch}>
          <LogOut aria-hidden className="size-3.5" />
          {t('decisionSheet.standalone.switchAccount')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {canAnswer
          ? t('decisionSheet.standalone.canAnswer')
          : open
            ? t('decisionSheet.standalone.viewOnly')
            : t('decisionSheet.standalone.notOpen')}
      </p>
    </section>
  );
}
